import { useMemo, useState } from 'react';
import type { ReferencePreview } from '../utils/transformToolsData';
import { filterHierarchy } from '../utils/filterHierarchy';

export function useToolListFilters(hierarchy: ReferencePreview[]): {
  search: string;
  referenceFilter: string[];
  healthFilter: string | null;
  displayLimit: number;
  filteredHierarchy: ReferencePreview[];
  hasMoreResults: boolean;
  handleSearchChange: (value: string) => void;
  handleReferenceChange: (values: string[]) => void;
  handleHealthChange: (value: string | null) => void;
  handleResetFilters: () => void;
  handleShowMore: () => void;
} {
  const [search, setSearch] = useState('');
  const [referenceFilter, setReferenceFilter] = useState<string[]>([]);
  const [healthFilter, setHealthFilter] = useState<string | null>(null);
  const [displayLimit, setDisplayLimit] = useState(10);

  const handleSearchChange = (value: string) => setSearch(value);
  const handleReferenceChange = (values: string[]) => setReferenceFilter(values);
  const handleHealthChange = (value: string | null) => setHealthFilter(value);
  const handleResetFilters = () => {
    setSearch('');
    setReferenceFilter([]);
    setHealthFilter(null);
  };
  const handleShowMore = () => setDisplayLimit((prev) => prev + 10);

  const filteredHierarchy = useMemo(() => {
    const filtered = filterHierarchy(hierarchy, referenceFilter, healthFilter, search);

    const sorted = filtered.sort((a, b) => {
      const getMaxDate = (ref: typeof a) => {
        const dates = ref.tools
          .map((t) => (t.lastTraceDate === null ? 0 : new Date(t.lastTraceDate).getTime()))
          .filter((d) => !isNaN(d));
        return dates.length > 0 ? Math.max(...dates) : 0;
      };

      const aMaxDate = getMaxDate(a);
      const bMaxDate = getMaxDate(b);
      return bMaxDate - aMaxDate;
    });

    return sorted.slice(0, displayLimit);
  }, [hierarchy, referenceFilter, healthFilter, search, displayLimit]);

  const hasMoreResults = useMemo(
    () => filterHierarchy(hierarchy, referenceFilter, healthFilter, search).length > displayLimit,
    [hierarchy, referenceFilter, healthFilter, search, displayLimit]
  );

  return {
    search,
    referenceFilter,
    healthFilter,
    displayLimit,
    filteredHierarchy,
    hasMoreResults,
    handleSearchChange,
    handleReferenceChange,
    handleHealthChange,
    handleResetFilters,
    handleShowMore,
  };
}
